import React from 'react';
import { Container, Typography, Box, useTheme, Alert, Paper, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';

const contentUseCase = new ContentUseCase();

/**
 * Public account/data deletion page.
 *
 * Required by Google Play's Data deletion policy: a publicly reachable URL,
 * without login, that names the app and developer, describes both deletion
 * routes (in-app and by request), and states which data is deleted, which is
 * retained and for how long. Content mirrors what deleteUserAccount actually
 * does in the backend (mecfs-cms: utils/delete-user-account.ts).
 */
export const AccountDeletionPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const lp = useLocalizedPath();
  const { accountDeletion } = contentUseCase.getLegalContent(language);

  const renderSteps = (steps: string[]) => (
    <Box component="ol" sx={{ pl: 3, m: 0 }}>
      {steps.map((step, idx) => (
        <li key={idx}>
          <Typography variant="body1" color="text.primary" sx={{ mb: 0.5 }}>
            {step}
          </Typography>
        </li>
      ))}
    </Box>
  );

  const renderList = (items: string[]) => (
    <ul>
      {items.map((item, idx) => (
        <li key={idx}>
          <Typography variant="body1" color="text.primary">
            {item}
          </Typography>
        </li>
      ))}
    </ul>
  );

  return (
    <Container maxWidth="md" sx={{ py: theme.spacing(8) }}>
      <SEO page="accountDeletion" />

      <Typography variant="h2" component="h1" gutterBottom align="center" color="primary">
        {accountDeletion.title}
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: theme.spacing(6) }}>
        {accountDeletion.subtitle}
      </Typography>

      <Typography variant="body1" paragraph color="text.primary">
        {accountDeletion.intro}
      </Typography>

      <Alert severity="warning" sx={{ mb: theme.spacing(6) }}>
        {accountDeletion.warning}
      </Alert>

      {/* Route 1 — in-app */}
      <Paper variant="outlined" sx={{ p: theme.spacing(3), mb: theme.spacing(4), backgroundColor: 'transparent' }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.inApp.title}
        </Typography>
        <Typography variant="body1" paragraph color="text.primary">
          {accountDeletion.inApp.description}
        </Typography>
        {renderSteps(accountDeletion.inApp.steps)}
        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }} color="text.secondary">
          {accountDeletion.inApp.note}
        </Typography>
      </Paper>

      {/* Route 2 — by email */}
      <Paper variant="outlined" sx={{ p: theme.spacing(3), mb: theme.spacing(6), backgroundColor: 'transparent' }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.byEmail.title}
        </Typography>
        <Typography variant="body1" paragraph color="text.primary">
          {accountDeletion.byEmail.description}
        </Typography>
        {renderSteps(accountDeletion.byEmail.steps)}
        <Typography variant="body1" sx={{ mt: 2 }} color="text.primary">
          {accountDeletion.byEmail.emailLabel}:{' '}
          <MuiLink
            href={`mailto:${accountDeletion.byEmail.email}?subject=${encodeURIComponent(
              accountDeletion.byEmail.emailSubject
            )}`}
            color="primary"
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            {accountDeletion.byEmail.email}
          </MuiLink>
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }} color="text.secondary">
          {accountDeletion.byEmail.note}
        </Typography>
      </Paper>

      {/* Partial deletion — Play's optional "remove some data without deleting the account" URL */}
      <Box sx={{ mb: theme.spacing(6) }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.partial.title}
        </Typography>
        <Typography variant="body1" paragraph color="text.primary">
          {accountDeletion.partial.description}
        </Typography>
        {accountDeletion.partial.items.map((item, idx) => (
          <Box key={idx} sx={{ mb: theme.spacing(3) }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }} color="text.primary">
              {item.label}
            </Typography>
            <Typography
              variant="body2"
              color="primary"
              sx={{ fontFamily: 'monospace', my: 0.5, wordBreak: 'break-word' }}
            >
              {item.path}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.effect}
            </Typography>
          </Box>
        ))}
        <Alert severity="info" sx={{ mt: theme.spacing(2), mb: theme.spacing(2) }}>
          {accountDeletion.partial.healthNote}
        </Alert>
        <Typography variant="body1" color="text.primary">
          {accountDeletion.partial.byRequest}
        </Typography>
      </Box>

      {/* Export first */}
      <Box sx={{ mb: theme.spacing(6) }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.exportFirst.title}
        </Typography>
        <Typography variant="body1" color="text.primary">
          {accountDeletion.exportFirst.description}
        </Typography>
      </Box>

      {/* What gets deleted */}
      <Box sx={{ mb: theme.spacing(6) }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.deleted.title}
        </Typography>
        <Typography variant="body1" paragraph color="text.primary">
          {accountDeletion.deleted.description}
        </Typography>
        {renderList(accountDeletion.deleted.items)}
      </Box>

      {/* What is retained */}
      <Box sx={{ mb: theme.spacing(6) }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.retained.title}
        </Typography>
        <Typography variant="body1" paragraph color="text.primary">
          {accountDeletion.retained.description}
        </Typography>
        {renderList(accountDeletion.retained.items)}
      </Box>

      {/* Inactive accounts */}
      <Box sx={{ mb: theme.spacing(6) }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.automatic.title}
        </Typography>
        <Typography variant="body1" color="text.primary">
          {accountDeletion.automatic.description}
        </Typography>
      </Box>

      {/* Contact */}
      <Box sx={{ mb: theme.spacing(4) }}>
        <Typography variant="h4" gutterBottom color="primary">
          {accountDeletion.contact.title}
        </Typography>
        <Typography variant="body1" paragraph color="text.primary">
          {accountDeletion.contact.description}
        </Typography>
        <MuiLink component={Link} to={lp('/privacy')} color="primary" underline="hover" sx={{ fontWeight: 600 }}>
          {accountDeletion.contact.privacyLinkText}
        </MuiLink>
      </Box>
    </Container>
  );
};
